import {all, call, put, select, takeEvery, takeLatest, delay} from 'redux-saga/effects';
import {
    BILLING_CYCLE_CHANGED,
    CHANGE_LAYOUT, DEVICES_CHANGED,
    DOWNLOAD_ACCOUNT_MANAGER_REPORT,
    GET_ACCOUNT_MANAGERS,
    GET_ACCOUNTS,
    GET_ADMIN_DATA, GET_ARCS,
    GET_BEACONS,
    GET_DEVICES,
    GET_PROMS,
    GET_SITES, GET_SITES_AND_BEACONS, SET_DEVICE_FILTER,
    SET_SELECTED_ACCOUNT, SET_V4_SEARCH,
} from './constants';
import {AdminAPI} from '../../utils/API/AdminAPI';
import {
    accountManagersChanged,
    accountsChanged, arcsChanged,
    beaconsChanged,
    devicesChanged, geofencesChanged,
    promsChanged, setSelectedAccount, sitesChanged, teamsChanged, v4SearchSettled,
} from './actions';
import {selectAccounts, selectBeacons as selectBeaconsState, selectDeviceFilter, selectLayout, selectSelectedAccount, selectSites as selectSitesState, selectV4Search} from './selectors';
import saveAs from 'file-saver';
import {selectCurrentUser} from '../../redux/selectors/app.selectors';
import {getCustomers, getSubscriptions} from "../../redux/actions/administration.actions";
import {customConfigsChanged} from "../AdminDevicePage/actions";
import {getMergedARCs} from "../V4ARC/arcs";
import {fetchV4Teams} from "../V4Teams/teams";
import {fetchV4Geofences} from "../V4Geofences/geofences";
import {fetchV4Site, fetchV4Sites} from "../V4Sites/sites";
import {fetchV4Beacon, fetchV4Beacons} from "../V4Beacons/beacons";
import v4api, {getV4SiteUuid, mapV4AccountToV3, v4ScopeFor, v4ScopeForUuid, v4SessionSafe, v4Unscoped} from "../../api/V4client";
import {buildV4AccountTree} from "../../utils/v4AccountTree";
import Device from "../../utils/models/Device";
import {mapV4DeviceToV3} from "../V4ARC/transforms";
import Team from "../../utils/models/Team";
import Geofence from "../../utils/models/Geofence";
import Site from "../../utils/models/Site";
import Beacon from "../../utils/models/Beacon";
import Account from "../../utils/models/Account";
import {selectDevices as selectPlatformDevices} from "../../redux/selectors/device.selector";
import {SET_DEVICE_AVAILABILITY, SET_DEVICE_ONLINE, WS_DATA_DEVICES} from "../../redux/constants/device.constants";
import {applyStatusAction} from "../../redux/reducers/device.reducer";
import {dataReceived} from "../../redux/actions/app.actions";
import {openSnackbar} from "../../redux/actions/snackbar.actions";
import {formatApiError} from "../../utils/v4ApiError";
import {groupV4SearchRefs, isV4SearchMatch, sortBySearch, V4_SEARCH_LIMIT, V4SearchEntity, v4SearchTooLong} from "../../utils/v4Search";

const api = new AdminAPI();

function* saveLayout() {
    const layout = yield select(selectLayout());
    localStorage.setItem(CHANGE_LAYOUT, layout);
}

// A V4-native account manager has no V3 token, so every AdminAPI call here
// would 401 — these sagas take their data from V4 instead.
function* isV4NativeSession() {
    const user = yield select(selectCurrentUser());
    return !!user?.isV4Native;
}

// V4 account uuid -> name, for labelling devices.
let v4AccountNames = {};
// Managed account uuid -> a promise of its devices, so callers share one request.
const v4DevicesByAccount = new Map();
// Accounts we couldn't read — unknown, not zero.
const v4DeviceFetchFailed = new Set();
const OWN_DEVICES_KEY = '__own__';
// Mutating a device must drop the cache, or the grid re-renders the stale row.
export function clearV4DeviceCache() {
    v4DevicesByAccount.clear();
    v4DeviceFetchFailed.clear();
}

// Creating or mutating a device must drop the cache, or the grid re-renders the
// stale list and the new row never shows.
export function invalidateV4DeviceCache(accountUuid) {
    if (accountUuid) {
        v4DevicesByAccount.delete(accountUuid);
        v4DeviceFetchFailed.delete(accountUuid);
    } else {
        v4DevicesByAccount.clear();
        v4DeviceFetchFailed.clear();
    }
}

// Or the next getDevices publishes the status the cached fetch saw.
function patchV4DeviceCache(action) {
    v4DevicesByAccount.forEach((pending, key) => {
        v4DevicesByAccount.set(key, pending.then(list => applyStatusAction(list, action)));
    });
}

// Active or archived, never both. A failure empties only its own list.
// `unscoped`: inside an entered manager a scoped list answers with its parent and siblings.
function* fetchV4AccountList(query, unscoped) {
    try {
        const res = unscoped
            ? yield call([v4api, v4api.accountsList], query, v4Unscoped())
            : yield call([v4api, v4api.accountsList], query);
        return res?.data || [];
    } catch (e) {
        console.warn(`Failed to fetch ${query?.archived ? 'archived ' : ''}V4 accounts`, e);
        return [];
    }
}

function* getV4NativeAccounts() {
    const user = yield select(selectCurrentUser());
    const unscoped = !!user?.isAccountManager;
    // Archived rows share the slice, as on V3.
    const [active, archived] = yield all([
        call(fetchV4AccountList, undefined, unscoped),
        call(fetchV4AccountList, {archived: true}, unscoped),
    ]);
    // By id, in case the archived query is ignored.
    const byId = new Map();
    [...active, ...archived].forEach(a => {
        if (a?.id && !a.deleted_at && !byId.has(a.id)) byId.set(a.id, a);
    });
    let v4Accounts = [...byId.values()];
    if (user?.isV4ManagerScoped) {
        const tree = buildV4AccountTree(v4Accounts, user.v4RootAccountId);
        v4Accounts = v4Accounts.filter(a => tree.has(a.id));
    }
    v4DevicesByAccount.clear();
    v4DeviceFetchFailed.clear();
    v4AccountNames = {};
    v4Accounts.forEach(a => { v4AccountNames[a.id] = a.name || ''; });
    yield put(accountsChanged(v4Accounts.map(a => new Account(mapV4AccountToV3(a)))));
}

// Never rejects — a cached rejection would resurface in every later caller. Session-safe:
// the fan-out reaches accounts this token may not read, and that 401 is a refusal.
function fetchV4AccountDevices(accountUuid) {
    if (v4DevicesByAccount.has(accountUuid)) return v4DevicesByAccount.get(accountUuid);
    const pending = v4api.devicesList(v4SessionSafe(v4ScopeForUuid(accountUuid)))
        .then(res => {
            v4DeviceFetchFailed.delete(accountUuid);
            return (res?.data || []).map(d => new Device(mapV4DeviceToV3(d)));
        })
        .catch(e => {
            console.warn(`Failed to fetch V4 devices for account ${accountUuid}`, e);
            v4DeviceFetchFailed.add(accountUuid);
            return [];
        });
    v4DevicesByAccount.set(accountUuid, pending);
    return pending;
}

// The session's own devices, which V4 answers unscoped; the bootstrap seed is never
// rewritten, so re-read it. Resolves null on failure and drops itself, so we retry.
function fetchV4OwnDevices() {
    if (v4DevicesByAccount.has(OWN_DEVICES_KEY)) return v4DevicesByAccount.get(OWN_DEVICES_KEY);
    const pending = v4api.devicesList()
        .then(res => (res?.data || []).map(d => new Device(mapV4DeviceToV3(d))))
        .catch(() => {
            v4DevicesByAccount.delete(OWN_DEVICES_KEY);
            return null;
        });
    v4DevicesByAccount.set(OWN_DEVICES_KEY, pending);
    return pending;
}

// Unscoped, the token only returns its own account devices — hence the per-account scope.
function* getV4NativeDevices() {
    const search = yield select(selectV4Search());
    if (search?.entity === V4SearchEntity.Devices) {
        yield* searchV4NativeDevices(search.term);
        return;
    }
    const selectedAccount = yield select(selectSelectedAccount());
    let list;
    if (selectedAccount?.id) {
        list = yield fetchV4AccountDevices(selectedAccount.id);
    } else {
        list = yield fetchV4OwnDevices();
        if (!list) {
            const devices = yield select(selectPlatformDevices());
            list = devices?.toArray ? devices.toArray() : (devices || []);
        }
    }
    // The V3 path filters server-side; match the same fields here.
    const filter = (yield select(selectDeviceFilter()) || '').lc();
    const matches = d => [d.get('name'), d.get('uniqueId'), d.get('accountName')]
        .join('').lc().indexOf(filter) >= 0;
    const labelled = list.map(d => {
        const owner = d.get('v4OwnerId');
        if (!owner) return d;
        // Key devices on their owning V4 account so filtering by account works.
        return d.set('accountId', owner).set('accountName', v4AccountNames[owner] || '');
    });
    yield put(devicesChanged(labelled
        .filter(d => !selectedAccount || d.get('accountId') === selectedAccount.id)
        .filter(d => !filter || matches(d))
    ));

    yield* getV4NativeDeviceCounts();
}

const COUNT_FANOUT = 6;

// V4 lists devices one account at a time, so the grid's device column has to ask every
// account: derived from the list on screen it only knew that list's account, leaving
// every other row blank. Cache-backed — only the first pass after a mutation refetches.
function* getV4NativeDeviceCounts() {
    const accounts = yield select(selectAccounts());
    const list = accounts?.toArray ? accounts.toArray() : (accounts || []);
    // The websocket seed can beat the account list in; keep what we have.
    if (!list.length) return;
    // Not worth a request per archived account.
    const ids = list.filter(a => !a.isArchived).map(a => a.get('id')).filter(Boolean);

    const counts = {};
    const counted = new Set();
    for (let i = 0; i < ids.length; i += COUNT_FANOUT) {
        const batch = ids.slice(i, i + COUNT_FANOUT);
        const devices = yield all(batch.map(id => call(fetchV4AccountDevices, id)));
        batch.forEach((id, n) => {
            // Blank beats asserting a 0 we never read.
            if (v4DeviceFetchFailed.has(id)) return;
            // Seed it, or an account that lost its last device renders blank again.
            counts[id] = counts[id] || {total: 0};
            devices[n].forEach(d => {
                if (d.isWebUser) return;
                // A scoped list can carry sub-account devices: bucket on the owner, once each.
                const deviceId = d.get('id');
                if (deviceId) {
                    if (counted.has(deviceId)) return;
                    counted.add(deviceId);
                }
                const owner = d.get('v4OwnerId') || id;
                counts[owner] = counts[owner] || {total: 0};
                counts[owner].total += 1;
            });
        });
    }
    yield put(dataReceived('deviceCounts', counts));
}

// The admin page can mount before the bootstrap finishes seeding.
function* refreshV4NativeDevices() {
    if (!(yield* isV4NativeSession())) return;
    yield* getV4NativeDevices();
}

function* getData() {
    const currentUser = yield select(selectCurrentUser());
    if (!currentUser) return;
    if (currentUser.isV4Native) {
        yield* getV4NativeAccounts();
        yield* getV4NativeDevices();
        // getMergedARCs swallows the V3 half, so this yields the V4 ARCs alone.
        yield* getARCs();
        // The customers page has no mount fetch of its own.
        yield put(getCustomers(true));
        // Sites first: beacons derive their floor plan from them.
        yield* getSites();
        yield* getBeacons();
        return;
    }
    if (currentUser.isSuperAdmin) {
        yield put(getCustomers(true));
        yield put(getSubscriptions(true));
    }
    yield all([
        getAccounts(),
        getDevices(),
        getAccountManagers(),
        getProms(),
        getARCs(),
        getSitesThenBeacons(),
        getTeams(),
        getGeofences(),
    ]);
}

// Beacons derive their floor plan from the sites, so the two must not race.
function* getSitesThenBeacons(action) {
    // A search left on logout must not reach the V3 calls.
    if (action?.type === SET_V4_SEARCH && !(yield* isV4NativeSession())) return;
    yield* getSites();
    yield* getBeacons();
}

function* getAccounts() {
    if (yield* isV4NativeSession()) {
        yield* getV4NativeAccounts();
        // The reload drops the device cache — rebuild the counts.
        yield* getV4NativeDeviceCounts();
        return;
    }
    const accounts = yield api.getAccounts();
    yield put(accountsChanged(accounts));
}

function* getDevices(action) {
    if (yield* isV4NativeSession()) {
        yield* getV4NativeDevices();
        return;
    }
    if (action?.type === SET_V4_SEARCH) return;
    const filter = yield select(selectDeviceFilter());
    const selectedAccount = yield select(selectSelectedAccount());
    const devices = yield api.getDevices(filter, selectedAccount?.id);
    yield put(devicesChanged(devices));
}

// A failed load re-publishes the current rows: it settles the spinner without
// blanking the grid, where [] would present a failure as data.
function* keepBeacons() {
    const current = yield select(selectBeaconsState());
    yield put(beaconsChanged(current?.toArray ? current.toArray() : (current || [])));
}

function* keepSites() {
    const current = yield select(selectSitesState());
    yield put(sitesChanged(current?.toArray ? current.toArray() : (current || [])));
}

// undefined = no V4 half on this session; null = no header, i.e. the token's own account.
export function* resolveV4AdminScope() {
    const user = yield select(selectCurrentUser());
    const selectedAccount = yield select(selectSelectedAccount());
    if (user?.isV4Native) {
        return selectedAccount?.id ? v4ScopeForUuid(selectedAccount.id) : null;
    }
    if (!selectedAccount?.id) return undefined;
    try {
        return yield call(() => v4ScopeFor(selectedAccount.id));
    } catch (_) {
        return undefined;
    }
}

const sameAccount = (a, b) => !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

// Follows a V4 row created in, or moved to, an account the grid doesn't read, as a device save
// does. True when it did: SET_SELECTED_ACCOUNT reloads sites and beacons, so skip the reload.
export function* followV4SavedAccount(record) {
    const user = yield select(selectCurrentUser());
    const accountId = record?.get?.('accountId');
    if (!user?.isV4Native || !accountId) return false;
    // A saved row follows only a changed account.
    const owner = record.get('v4OwnerId');
    if ((record.get('uuid') || record.get('id')) && (!owner || sameAccount(accountId, owner))) return false;
    const selectedAccount = yield select(selectSelectedAccount());
    const own = user.v4ScopedAccountId;
    if (sameAccount(accountId, selectedAccount?.id || own)) return false;
    // No selection reads the own (or entered) account.
    if (sameAccount(accountId, own)) {
        yield put(setSelectedAccount(null));
        return true;
    }
    const accounts = yield select(selectAccounts());
    const list = accounts?.toArray ? accounts.toArray() : (accounts || []);
    const account = list.find(a => sameAccount(a?.id, accountId));
    if (!account) return false;
    yield put(setSelectedAccount(account));
    return true;
}

// Keys V4 rows in the page's account namespace (picked numeric id on V3, uuid on V4-native)
// so every accountId comparison matches; the owner uuid stays on v4OwnerId for writes.
function labelWithV4Account(row, selectedAccount, isV4Native) {
    const owner = row.get('v4OwnerId');
    const accountId = (!isV4Native && selectedAccount?.id) || owner || selectedAccount?.id;
    if (!accountId) return row;
    const name = v4AccountNames[owner]
        || selectedAccount?.name
        || row.get('accountName');
    return row.set('accountId', accountId).set('accountName', name || '');
}

const normaliseAddress = mac => (mac || '').replace(/[^0-9a-zA-Z]/g, '').toLowerCase();

const fieldOf = (row, key) => (row?.get ? row.get(key) : row?.[key]);

// The admin site list spans tenants; unnarrowed, a V4 beacon binds to another tenant's plan.
function siteScopeFor(sites, accountId) {
    const list = sites?.toArray ? sites.toArray() : (sites || []);
    if (!accountId) return list;
    return list.filter(s => String(fieldOf(s, 'accountId') ?? '') === String(accountId));
}

function* getBeacons() {
    const search = yield* currentV4Search();
    // Beacons bind to the account's sites, which a site search replaced.
    if (search?.entity === V4SearchEntity.Sites) return;
    if (search?.entity === V4SearchEntity.Beacons) {
        yield* searchV4NativeBeacons(search.term);
        return;
    }
    const user = yield select(selectCurrentUser());
    const selectedAccount = yield select(selectSelectedAccount());
    const scope = yield* resolveV4AdminScope();

    let v4Beacons = null;
    if (scope !== undefined) {
        const sites = yield select(selectSitesState());
        const ownSites = siteScopeFor(sites, selectedAccount?.id);
        const rows = yield call(fetchV4Beacons, scope, ownSites, v4AccountNames);
        // null = the request failed, [] = none.
        v4Beacons = rows && rows.map(
            b => labelWithV4Account(new Beacon(b), selectedAccount, user?.isV4Native),
        );
    }

    if (user?.isV4Native) {
        if (!v4Beacons) {
            yield put(openSnackbar('Could not load V4 beacons'));
            yield* keepBeacons();
            return;
        }
        yield put(beaconsChanged(v4Beacons));
        return;
    }

    // Guarded: BEACONS_CHANGED is the only thing that clears the grid spinner.
    let v3Beacons;
    try {
        v3Beacons = yield api.getBeacons();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch V3 beacons', e);
        yield put(openSnackbar('Could not load beacons'));
        if (v4Beacons?.length) yield put(beaconsChanged(v4Beacons));
        else yield* keepBeacons();
        return;
    }
    // Best-effort on V3, like teams and geofences: no V4 notice.
    if (!v4Beacons?.length) {
        yield put(beaconsChanged(v3Beacons || []));
        return;
    }
    // MAC is the only V3/V4 beacon bridge and is unique only within an account,
    // so the V3 side is narrowed to the account the V4 half was fetched for.
    const v3Addresses = new Set(
        (v3Beacons || [])
            .filter(b => !selectedAccount?.id
                || String(b.get('accountId')) === String(selectedAccount.id))
            .map(b => normaliseAddress(b.get('mac')))
            .filter(Boolean),
    );
    const additions = v4Beacons.filter(b => {
        const address = normaliseAddress(b.get('mac'));
        return !address || !v3Addresses.has(address);
    });
    yield put(beaconsChanged([...(v3Beacons || []), ...additions]));
}

function* getTeams() {
    // Fetch V3 teams (legacy) and V4 teams scoped to the selected sub-account
    // in parallel and concatenate. V4 teams are tagged with `source: 'v4'` so
    // downstream code can branch save / device-link paths.
    const selectedAccount = yield select(selectSelectedAccount());
    const v3Promise = api.getTeams();
    const v4Promise = (async () => {
        if (!selectedAccount?.id) return [];
        try {
            const scope = await v4ScopeFor(selectedAccount.id);
            return await fetchV4Teams(scope);
        } catch (e) {
            return [];
        }
    })();
    const [v3, v4] = yield all([v3Promise, v4Promise]);
    const v4Records = (v4 || []).map(t => new Team(t));
    yield put(teamsChanged([...(v3 || []), ...v4Records]));
}

function* getGeofences() {
    const selectedAccount = yield select(selectSelectedAccount());
    const v3Promise = api.getGeofences();
    const v4Promise = (async () => {
        if (!selectedAccount?.id) return [];
        try {
            const scope = await v4ScopeFor(selectedAccount.id);
            return await fetchV4Geofences(scope, selectedAccount.id);
        } catch (e) {
            return [];
        }
    })();
    const [v3, v4] = yield all([v3Promise, v4Promise]);
    const v4Records = (v4 || []).map(g => new Geofence(g));
    yield put(geofencesChanged([...(v3 || []), ...v4Records]));
}

function* getSites() {
    const search = yield* currentV4Search();
    // The beacon search reads its own sites.
    if (search?.entity === V4SearchEntity.Beacons) return;
    if (search?.entity === V4SearchEntity.Sites) {
        yield* searchV4NativeSites(search.term);
        return;
    }
    const user = yield select(selectCurrentUser());
    const selectedAccount = yield select(selectSelectedAccount());
    const scope = yield* resolveV4AdminScope();

    let v4Sites = null;
    if (scope !== undefined) {
        const rows = yield call(fetchV4Sites, scope, v4AccountNames);
        // null = the request failed, [] = none.
        v4Sites = rows && rows.map(
            s => labelWithV4Account(new Site(s), selectedAccount, user?.isV4Native),
        );
    }

    if (user?.isV4Native) {
        if (!v4Sites) {
            yield put(openSnackbar('Could not load V4 sites'));
            yield* keepSites();
            return;
        }
        yield put(sitesChanged(v4Sites));
        return;
    }

    let v3Sites;
    try {
        v3Sites = yield api.getSites();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch V3 sites', e);
        yield put(openSnackbar('Could not load sites'));
        if (v4Sites?.length) yield put(sitesChanged(v4Sites));
        else yield* keepSites();
        return;
    }
    if (!v4Sites?.length) {
        yield put(sitesChanged(v3Sites || []));
        return;
    }
    // Drop migrated V4 twins: the V3 row is the one every `+id` comparison expects.
    const twinUuids = yield* resolveMigratedSiteUuids(v3Sites, selectedAccount?.id);
    const additions = v4Sites.filter(s => !twinUuids.has(s.get('uuid')));
    yield put(sitesChanged([...(v3Sites || []), ...additions]));
}

// getV4SiteUuid costs two requests per miss, so this only runs with a V4 half
// and is narrowed to its account.
function* resolveMigratedSiteUuids(v3Sites, accountId) {
    let list = (v3Sites?.toArray ? v3Sites.toArray() : (v3Sites || [])).filter(s => s?.id);
    if (accountId) {
        const scoped = list.filter(s => String(s.accountId) === String(accountId));
        // Keep the full list when no row carries an accountId, so dedup still runs.
        if (scoped.length || list.some(s => s.accountId)) list = scoped;
    }
    if (!list.length) return new Set();
    const uuids = yield all(list.map(s => call(() =>
        getV4SiteUuid(s.id).catch(() => null)
    )));
    return new Set(uuids.filter(Boolean));
}

function* currentV4Search() {
    if (!(yield* isV4NativeSession())) return null;
    return yield select(selectV4Search());
}

const lc = id => String(id ?? '').toLowerCase();

// Unscoped: a scope header limits the search to that one account, not its sub-accounts.
function* findV4Matches(entity, term) {
    const res = yield call(
        [v4api, v4api.searchList],
        {term, type: entity, limit: V4_SEARCH_LIMIT},
        v4SessionSafe(v4Unscoped()),
    );
    const refs = res?.data?.results?.[entity] || [];
    const user = yield select(selectCurrentUser());
    const accounts = yield select(selectAccounts());
    const list = accounts?.toArray ? accounts.toArray() : (accounts || []);
    const owners = new Set(list.filter(a => !a.isArchived).map(a => lc(a.get('id'))));
    if (user?.v4RootAccountId) owners.add(lc(user.v4RootAccountId));
    return {refs, hasMore: !!res?.data?.hasMore?.[entity], byOwner: groupV4SearchRefs(refs, owners)};
}

function* readInBatches(items, effectFor) {
    const results = [];
    for (let i = 0; i < items.length; i += COUNT_FANOUT) {
        results.push(...(yield all(items.slice(i, i + COUNT_FANOUT).map(effectFor))));
    }
    return results;
}

function* searchV4Accounts(entity, term, subject) {
    if (!term || v4SearchTooLong(term)) return null;
    try {
        return yield* findV4Matches(entity, term);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[${subject} search failed]`, e?.response?.status, e?.response?.data || e);
        yield put(openSnackbar(formatApiError('search', subject, e)));
        yield put(v4SearchSettled(entity, term, {failed: true}));
        return null;
    }
}

function* searchV4NativeDevices(term) {
    const found = yield* searchV4Accounts(V4SearchEntity.Devices, term, 'V4 devices');
    if (!found) {
        yield put(devicesChanged([]));
        return;
    }
    const owners = [...found.byOwner.keys()];
    const lists = yield* readInBatches(owners, owner => call(fetchV4AccountDevices, owner));
    const rows = [];
    owners.forEach((owner, n) => {
        lists[n].filter(d => isV4SearchMatch(found.byOwner.get(owner), d)).forEach(d => {
            const accountId = d.get('v4OwnerId') || owner;
            rows.push(d.set('accountId', accountId).set('accountName', v4AccountNames[accountId] || ''));
        });
    });
    if (owners.some(owner => v4DeviceFetchFailed.has(owner))) {
        yield put(openSnackbar('Could not load every V4 device found'));
    }
    yield put(devicesChanged(sortBySearch(rows, found.refs)));
    yield put(v4SearchSettled(V4SearchEntity.Devices, term, {hasMore: found.hasMore}));
}

// A 404 is a hit deleted since the search indexed it.
function* readV4Hit(fetchOne, {id, owner}, ...args) {
    try {
        return {row: yield call(fetchOne, id, v4ScopeForUuid(owner), ...args)};
    } catch (e) {
        if (e?.response?.status === 404) return {};
        // eslint-disable-next-line no-console
        console.warn('[V4 search hit read failed]', id, e?.response?.status, e?.response?.data || e);
        return {failed: true};
    }
}

function* searchV4NativeSites(term) {
    const found = yield* searchV4Accounts(V4SearchEntity.Sites, term, 'V4 sites');
    if (!found) {
        yield put(sitesChanged([]));
        return;
    }
    const hits = found.refs.filter(ref => ref?.id && found.byOwner.has(ref.owner));
    const reads = yield* readInBatches(hits, ref => call(readV4Hit, fetchV4Site, ref, v4AccountNames));
    if (reads.some(read => read.failed)) yield put(openSnackbar('Could not load every V4 site found'));
    yield put(sitesChanged(reads
        .filter(read => read.row)
        .map(read => labelWithV4Account(new Site(read.row), null, true))));
    yield put(v4SearchSettled(V4SearchEntity.Sites, term, {hasMore: found.hasMore}));
}

function* searchV4NativeBeacons(term) {
    const found = yield* searchV4Accounts(V4SearchEntity.Beacons, term, 'V4 beacons');
    if (!found) {
        yield put(beaconsChanged([]));
        return;
    }
    const owners = [...found.byOwner.keys()];
    // A V4 beacon finds its site by coordinate, so its account's sites come first.
    const lists = yield* readInBatches(owners, owner => call(fetchV4Sites, v4ScopeForUuid(owner), v4AccountNames));
    const sitesOf = new Map();
    owners.forEach((owner, n) => {
        if (lists[n]) sitesOf.set(owner, lists[n].map(s => labelWithV4Account(new Site(s), null, true)));
    });
    // Only hits with their account's sites: saved off its plan, a beacon would lose it.
    const hits = found.refs.filter(ref => ref?.id && sitesOf.has(ref.owner));
    const reads = yield* readInBatches(
        hits,
        ref => call(readV4Hit, fetchV4Beacon, ref, sitesOf.get(ref.owner), v4AccountNames),
    );
    if (sitesOf.size < owners.length || reads.some(read => read.failed)) {
        yield put(openSnackbar('Could not load every V4 beacon found'));
    }
    // The beacon form picks from this list, so the add form's account keeps its sites.
    const user = yield select(selectCurrentUser());
    const selectedAccount = yield select(selectSelectedAccount());
    const loaded = new Set([...sitesOf.keys()].map(lc));
    const kept = siteScopeFor(yield select(selectSitesState()), selectedAccount?.id || user?.v4ScopedAccountId)
        .filter(s => !loaded.has(lc(fieldOf(s, 'accountId'))));
    yield put(sitesChanged([...kept, ...[...sitesOf.values()].flat()]));
    yield put(beaconsChanged(reads
        .filter(read => read.row)
        .map(read => labelWithV4Account(new Beacon(read.row), null, true))));
    yield put(v4SearchSettled(V4SearchEntity.Beacons, term, {hasMore: found.hasMore}));
}

function* getAccountManagers() {
    const user = yield select(selectCurrentUser());
    if (user.isSuperAdmin) {
        const accountManagers = yield api.getAccountManagers();
        yield put(accountManagersChanged(accountManagers));
    }
}

function* getProms() {
    const user = yield select(selectCurrentUser());
    if (user.isSuperAdmin || user.isAccountManager) {
        const proms = yield api.getProms();
        yield put(promsChanged(proms));
    }
}

function* getARCs() {
    const user = yield select(selectCurrentUser());
    if (!user.isSuperAdmin && !user.isAccountManager) return;
    // Fetch V3 + V4 ARCs in parallel and render as a single list.
    const arcs = yield getMergedARCs();
    yield put(arcsChanged(arcs));
}

function* downloadAccountManagerReport({billingCycle, fileName}) {
    const data = yield api.downloadAccountManagerReport(billingCycle.month() + 1, billingCycle.year());
    saveAs(new Blob([data]), fileName);
}

function *getCustomConfigs({devices}) {
    if (yield* isV4NativeSession()) {
        // Settle the slice: a bare return leaves `fetched` false forever, and
        // anything keyed on it spins.
        yield put(customConfigsChanged({}));
        return;
    }
    yield delay(3000);
    yield put(customConfigsChanged(null));
    const customConfigs = yield api.getCustomConfigs(devices.map(d => d.id));
    yield put(customConfigsChanged(customConfigs));
}

const searchOf = (...entities) => action => action.type === SET_V4_SEARCH && entities.includes(action.entity);

export default function* sendSaga() {
    yield takeEvery(CHANGE_LAYOUT, saveLayout);
    yield takeEvery(GET_ADMIN_DATA, getData);
    // Last intent wins: a transfer moves the scope three ways at once (this saga,
    // the follow-the-device navigate, the :accountId route sync), and under
    // takeEvery whichever response landed last published its rows — a superseded
    // fetch could leave the old account's devices under the new account's filter.
    yield takeLatest([
        GET_DEVICES, SET_DEVICE_FILTER, SET_SELECTED_ACCOUNT, BILLING_CYCLE_CHANGED,
        searchOf(V4SearchEntity.Devices),
    ], getDevices);
    // An account switch must reload sites/beacons, and only the latest may publish,
    // or one account's floor plans show under another's selection.
    yield takeLatest([
        GET_SITES_AND_BEACONS, SET_SELECTED_ACCOUNT,
        searchOf(V4SearchEntity.Sites, V4SearchEntity.Beacons),
    ], getSitesThenBeacons);
    yield takeEvery(GET_BEACONS, getBeacons);
    yield takeEvery(GET_ACCOUNTS, getAccounts);
    yield takeEvery(GET_ACCOUNT_MANAGERS, getAccountManagers);
    yield takeEvery(GET_SITES, getSites);
    yield takeEvery(GET_PROMS, getProms);
    yield takeEvery(GET_ARCS, getARCs);
    yield takeLatest(DEVICES_CHANGED, getCustomConfigs);
    yield takeLatest(WS_DATA_DEVICES, refreshV4NativeDevices);
    yield takeEvery([SET_DEVICE_ONLINE, SET_DEVICE_AVAILABILITY], function* (action) {
        yield call(patchV4DeviceCache, action);
    });
    yield takeEvery(BILLING_CYCLE_CHANGED, getAccounts);
    yield takeEvery(DOWNLOAD_ACCOUNT_MANAGER_REPORT, downloadAccountManagerReport);
}
